import { Alert, AlertAction, AlertDescription, AlertTitle, Button } from '@monksflow/monks-ui';
import { CircleCheckIcon, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { SuccessBannerProps } from './SuccessBanner.types';

/**
 * Inline success banner with dismiss button and optional action buttons.
 * Renders inline (not a toast). Visibility controlled by the parent component.
 *
 * Tessra's `Alert` ships only `default` and `destructive` variants, so the success surface comes
 * from the `positive` colour tokens rather than a variant.
 */
export function SuccessBanner({ title, description, onClose, actions }: SuccessBannerProps) {
  const { t } = useTranslation('common');

  return (
    <Alert
      aria-live="polite"
      icon={<CircleCheckIcon />}
      className="items-start rounded-2xl border-positive bg-positive p-4 text-positive-foreground"
    >
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>{description}</AlertDescription>
      {actions && <div className="mt-3 flex flex-row gap-2">{actions}</div>}
      <AlertAction>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onClose}
          aria-label={t('components.successBanner.dismissBanner')}
        >
          <XIcon />
        </Button>
      </AlertAction>
    </Alert>
  );
}
